/* eslint-disable @typescript-eslint/no-explicit-any */
import cls from 'classnames';
import getConfig from 'next/config';
import * as R from 'ramda';
import { forwardRef } from 'react';

import { AdPlacements, EVENT_PAGE_SLUGS } from '@/lib/constants';
import { findParentElement, isEnvVarTrue, notNull } from '@/lib/utils';
import { AdChannel, AdSlot } from '@/types/views/generic';

import AdSlotsConfig from './ad-slots';

const registeredAdContainers = new Set<string>();

const resetAdServerRegistration = () => {
  registeredAdContainers.clear();
  window.admTagMan?.pApi?.resetAll?.();
};

const getSlotKey = (adContainerId: string) => {
  const configEntry = Object.entries(AdSlotsConfig).find(
    ([, value]) => value.container === adContainerId,
  );

  return configEntry ? (configEntry[0] as AdSlot) : null;
};

const isChannelSwitcherContainer = (id: string) =>
  id.includes('channel-switcher');

const clampClientSideSideAd = (value: number, axis: 'width' | 'height') =>
  Math.min(
    Math.max(value, axis === 'width' ? 120 : 250),
    axis === 'width' ? 300 : 600,
  );

const getDynamicTargetingPos = (containerId: string, slotKey: AdSlot) => {
  if (!isChannelSwitcherContainer(containerId)) {
    return R.last(containerId.split('-')) ?? '1';
  }

  if (slotKey === 'top_ad_1') {
    return containerId;
  }

  return '1';
};

const shouldRegisterAdSlot = (
  slotKey: AdSlot,
  channel: AdChannel,
  adsDevice: string,
  containerId: string,
) =>
  isChannelSwitcherContainer(containerId)
    ? (slotKey === 'side-right_ad_1' && adsDevice === 'Desktop') ||
      Boolean(AdPlacements[channel][adsDevice]?.includes(slotKey))
    : slotKey !== 'side-right_ad_1' &&
      Boolean(AdPlacements[channel][adsDevice]?.includes(slotKey));

export const clearChannelSwitcherAds = () => {
  document
    .querySelectorAll('.ad-container[id*="channel-switcher"]')
    .forEach((node) => {
      const element = node;
      registeredAdContainers.delete(element.id);
      element.classList.remove('adm-empty', 'adm-loaded', 'adm-error');
      element.innerHTML = '';
    });
};

const preparePageAdsForViewportChange = () => {
  Array.from(registeredAdContainers).forEach((id) => {
    if (!isChannelSwitcherContainer(id)) {
      registeredAdContainers.delete(id);
    }
  });

  document.querySelectorAll('.ad-container').forEach((node) => {
    const element = node;

    if (isChannelSwitcherContainer(element.id)) {
      return;
    }

    // Preserve welcome ad DOM (same as clearAds)
    if (element.id === 'ad-slot-top_special_1') {
      return;
    }

    element.classList.remove('adm-empty', 'adm-loaded', 'adm-error');
    element.innerHTML = '';
  });

  window.admTagMan?.pApi?.resetAll?.();
};

export const clearAds = () => {
  const adIds = Object.values(AdSlotsConfig).map(R.prop('container'));

  adIds.forEach((id) => {
    // Do not clear WelcomeAd top_special_1 which would otherwise lead to empty containers
    if (id === 'ad-slot-top_special_1') {
      return;
    }
    document.querySelectorAll(`[data-slotid="${id}"]`).forEach((node) => {
      const element = node;
      element.innerHTML = '';
    });
  });

  clearChannelSwitcherAds();
  resetAdServerRegistration();
};

const hideWatchExperienceAdContainer = (elementId: string) => {
  const adElement = document.getElementById(elementId);

  if (adElement) {
    const parentSection = findParentElement(adElement, 'tagName', 'ASIDE');

    if (parentSection) {
      parentSection.style.display = 'none';
    }
  }
};

const adjustChannelSwitcherContainerSize = (
  container: HTMLElement,
  data: { width?: number; height?: number },
) => {
  const adElement = container;
  const { width: rawWidth, height: rawHeight } = data;
  const bottomAdWrapper = adElement.closest('.channel-switcher-bottom-ad');

  if (bottomAdWrapper instanceof HTMLElement && rawHeight) {
    bottomAdWrapper.style.minHeight = `${rawHeight}px`;

    return;
  }

  const sideAdFrame = adElement.closest('.channel-switcher-side-ad');

  if (sideAdFrame instanceof HTMLElement) {
    if (rawWidth) {
      adElement.style.width = `${clampClientSideSideAd(rawWidth, 'width')}px`;
    }

    if (rawHeight) {
      const height = clampClientSideSideAd(rawHeight, 'height');
      sideAdFrame.style.height = `${height}px`;
      sideAdFrame.style.minHeight = `${height}px`;
    }
  }
};

const isAdContainerVisible = (element: Element): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  let current: HTMLElement | null = element;

  while (current) {
    const style = window.getComputedStyle(current);

    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }

    current = current.parentElement;
  }

  return true;
};

const showAndAdjustContainerSize = (elementId: string) => (data: unknown) => {
  if (typeof data !== 'object' || data === null) {
    return;
  }

  const adElement = document.getElementById(elementId);

  if (!adElement) {
    return;
  }

  if (isChannelSwitcherContainer(elementId)) {
    adjustChannelSwitcherContainerSize(adElement, data);
  }
};

const registerAds = (
  adContainers: Element[],
  channel: AdChannel,
  adsDevice: string,
  currentBreakpoint: string,
  dynamicAdPosition = false,
  onAdReady?: AdmTagMan['onEvent'],
  onAdEmpty?: AdmTagMan['onEvent'],
  onAdError?: AdmTagMan['onEvent'],
) => {
  if (!window.admTagMan) {
    return;
  }

  const posIndex = {
    index_ad_1: 1,
  };

  window.admTagMan.q.push(() => {
    adContainers.forEach((adContainer) => {
      const slotId = adContainer.getAttribute('data-slotid');
      const slotKey = getSlotKey(slotId || adContainer.id);

      const canRegister =
        slotKey &&
        shouldRegisterAdSlot(slotKey, channel, adsDevice, adContainer.id);

      if (!canRegister) {
        return;
      }

      // Do not register left sidebar if not in view
      if (
        !['2xl', '3xl'].includes(currentBreakpoint) &&
        slotKey === 'left_ad_1'
      ) {
        return;
      }

      // Right sidebar is hidden below lg via side-ad-box CSS; skip registration on
      // Desktop between md–lg where AdPlacements still lists this slot.
      if (
        !['lg', 'xl', '2xl', '3xl'].includes(currentBreakpoint) &&
        slotKey === 'right_ad_1'
      ) {
        return;
      }

      if (slotKey !== 'preroll_1' && !isAdContainerVisible(adContainer)) {
        return;
      }

      if (registeredAdContainers.has(adContainer.id)) {
        return;
      }

      // Remove adm-* classes and possible old ads
      // before (re-)registering (recommended by RIAD)
      adContainer.classList.remove('adm-empty', 'adm-loaded', 'adm-error');
      // eslint-disable-next-line no-param-reassign
      adContainer.innerHTML = '';

      const currentAdIndex = getDynamicTargetingPos(adContainer.id, slotKey);

      window.admTagMan.registerSlot({
        slot: slotKey,
        container: adContainer.id,
        events: {
          // Must be done twice because adReady data is sometimes insufficient
          adReady: (...args: unknown[]) => {
            showAndAdjustContainerSize(adContainer.id)(args[0]);
            onAdReady && onAdReady(...args);
          },
          adEmpty: (...args: unknown[]) => {
            if (channel === 'Watch') {
              hideWatchExperienceAdContainer(adContainer.id);
            }
            onAdEmpty && onAdEmpty(...args, adContainer.id);
          },
          adError: (...args: unknown[]) => {
            if (channel === 'Watch') {
              hideWatchExperienceAdContainer(adContainer.id);
            }
            onAdError && onAdError(...args);
          },
          adLoaded: (...args: unknown[]) => {
            showAndAdjustContainerSize(adContainer.id)(args[0]);
          },
        },
        targeting: {
          ...(!dynamicAdPosition && posIndex[slotKey as 'index_ad_1']
            ? // eslint-disable-next-line no-plusplus
              {
                pos: (posIndex[slotKey as 'index_ad_1']++).toString(),
              }
            : {}),
          ...(dynamicAdPosition && { pos: currentAdIndex }),
        },
      });

      registeredAdContainers.add(adContainer.id);
    });
  });

  window.admTagMan.q.push(() => {
    window.admTagMan.loadSlots();
  });
};

export const registerChannelSwitcherSlot = (
  container: HTMLDivElement,
  channel: AdChannel,
  isMobile: boolean,
  currentBreakpoint: string,
) => {
  if (!window.admTagMan) {
    return;
  }

  const isLoaded =
    container.classList.contains('adm-loaded') &&
    container.childElementCount > 0;

  if (registeredAdContainers.has(container.id) && isLoaded) {
    return;
  }

  if (registeredAdContainers.has(container.id)) {
    registeredAdContainers.delete(container.id);
  }

  registerAds(
    [container],
    channel,
    isMobile ? 'MobileWeb' : 'Desktop',
    currentBreakpoint,
    true,
  );
};

export const registerAd = (
  container: Element,
  channel: AdChannel,
  isMobile: boolean,
  currentBreakpoint: string,
  onAdReady?: AdmTagMan['onEvent'],
  onAdEmpty?: AdmTagMan['onEvent'],
  onAdError?: AdmTagMan['onEvent'],
) => {
  const adsDevice = isMobile ? 'MobileWeb' : 'Desktop';

  registerAds(
    [container],
    channel,
    adsDevice,
    currentBreakpoint,
    true,
    onAdReady,
    onAdEmpty,
    onAdError,
  );
};

export const updateAds = (
  channel: AdChannel,
  isMobile: boolean,
  currentBreakpoint: string,
  adSlotsToUpdate?: AdSlot[],
  additionalEntriesAmount?: number,
) => {
  const adsDevice = isMobile ? 'MobileWeb' : 'Desktop';
  const adContainerElements = document.querySelectorAll(
    '.ad-container:not(.adm-loaded)',
  );
  const filterAdSlotsToUpdate = (adContainer: Element) => {
    const slotAttributeId = adContainer.getAttribute('data-slotid');

    return adSlotsToUpdate?.some(
      (adSlot) => AdSlotsConfig[adSlot].container === slotAttributeId,
    );
  };

  const adContainers = (
    adSlotsToUpdate?.length
      ? Array.from(adContainerElements).filter(filterAdSlotsToUpdate)
      : Array.from(adContainerElements)
  ).filter((container) => !isChannelSwitcherContainer(container.id));

  const lastNAdContainers = adContainers.slice(
    additionalEntriesAmount ? -additionalEntriesAmount : 0,
  );

  registerAds(lastNAdContainers, channel, adsDevice, currentBreakpoint, true);
};

const findAndRegisterAds = (
  channel: AdChannel,
  currentBreakpoint: string,
  isCreatorProfilePage: boolean,
  adsDevice: 'MobileWeb' | 'Desktop',
  skippedSlots: AdSlot[] = [],
) => {
  // NOTE: Show NO ads on profile pages that are not creators
  if (channel === 'Profiles' && !isCreatorProfilePage) {
    return;
  }

  // Register slots
  const adContainerElements = document.getElementsByClassName('ad-container');

  const adContainers = Array.from(adContainerElements).filter((container) => {
    const slotId = container.getAttribute('data-slotid');
    const slotKey = getSlotKey(slotId || container.id);

    return (
      slotKey &&
      !skippedSlots.includes(slotKey) &&
      !isChannelSwitcherContainer(container.id)
    );
  });

  registerAds(adContainers, channel, adsDevice, currentBreakpoint);
};

const buildAdInitTargeting = (channel: AdChannel, isUserLoggedIn?: boolean) => {
  const { publicRuntimeConfig } = getConfig();
  const path = window.location.pathname;
  const targetingChannel = path
    .replace('/', '')
    .replace(
      /^(\w)(.+)/,
      (match, p1, p2) => p1.toUpperCase() + p2.toLowerCase(),
    );
  const articleId = path.substring(path.lastIndexOf('/artikel/') + 9);

  return {
    admforce: isEnvVarTrue(publicRuntimeConfig.adsQAMode) ? 'qa' : undefined,
    olid: isUserLoggedIn ? 2 : 0,
    ...(targetingChannel === 'Schlaumeier' && {
      channel: targetingChannel,
    }),
    ...(path.includes('/artikel/') && {
      articleid: articleId,
    }),
  };
};

export const initAds = (
  channel: AdChannel,
  isMobile: boolean,
  currentBreakpoint: string,
  isCreatorProfilePage: boolean,
  isUserLoggedIn?: boolean,
  afterInit?: () => void,
) => {
  if (!window.admTagMan) return;
  const adsDevice = isMobile ? 'MobileWeb' : 'Desktop';

  clearAds();

  window.admTagMan.q.push(() => {
    window.admTagMan.init({
      platform: adsDevice,
      channel,
      targeting: buildAdInitTargeting(channel, isUserLoggedIn),
    });
  });

  findAndRegisterAds(
    channel,
    currentBreakpoint,
    isCreatorProfilePage,
    adsDevice,
  );

  if (afterInit) {
    window.admTagMan.q.push(() => {
      afterInit();
    });
  }
};

export const updateAdPlatformForViewport = (
  channel: AdChannel,
  isMobile: boolean,
  currentBreakpoint: string,
  isCreatorProfilePage: boolean,
  isUserLoggedIn?: boolean,
  afterReady?: () => void,
) => {
  if (!window.admTagMan) return;

  const adsDevice = isMobile ? 'MobileWeb' : 'Desktop';

  clearChannelSwitcherAds();
  preparePageAdsForViewportChange();

  window.admTagMan.q.push(() => {
    window.admTagMan.init({
      platform: adsDevice,
      channel,
      targeting: buildAdInitTargeting(channel, isUserLoggedIn),
    });
  });

  window.admTagMan.q.push(() => {
    findAndRegisterAds(
      channel,
      currentBreakpoint,
      isCreatorProfilePage,
      adsDevice,
    );
    window.admTagMan.loadSlots();
  });

  if (afterReady) {
    window.admTagMan.q.push(() => {
      afterReady();
    });
  }
};

export const mountAdsHelperFunctionsOnWindow = () => {
  // @ts-ignore 'Ads' prop check
  if (window && typeof window?.Ads === 'undefined') {
    const getSidebar = (position: 'left' | 'right' = 'right') =>
      document.getElementById(`ads-papi-sidebar-${position}`);

    const alterStyle = (
      props: (keyof HTMLElement['style'])[],
      value: string,
    ) => {
      ['right', 'left']
        .map((side) => getSidebar(side as 'right' | 'left'))
        .filter(notNull)
        .forEach((element) => {
          props.forEach((prop) => {
            // @ts-ignore Only writable available
            // eslint-disable-next-line no-param-reassign
            element.style[prop] = value;
          });
        });
    };

    const setSideBar = () => {
      alterStyle(['width', 'height'] as (keyof CSSStyleDeclaration)[], '100%');
    };

    const resetAll = () => {
      alterStyle(['width', 'height'] as (keyof CSSStyleDeclaration)[], '');
    };

    /**
     * WelcomeAd functions which can be called from RiAd.
     * It can be used to implement our own welcomeAd behavior.
     * These functions can be executed within the browser console: e.g. window.top.Ads.setWelcomeAd()
     * Keeping it as might be useful someday.
     */

    const setWelcomeAd = () => {
      // window.top.Ads.helpers.setWelcomeAd()

      // on ios devices the welcome ad is only displayed partially. That's why this welcome ad container for mobile devices gets selected and scrolled into view
      const element = document.getElementById('ad-slot-top_special_1');

      if (element) {
        element.scrollIntoView();
      }
    };

    const closeWelcomeAd = () => {
      // window.top.Ads.helpers.closeWelcomeAd();
      // eslint-disable-next-line no-console
      console.info('Close Welcome Ad Container');
    };

    const scrollWelcomeAd = () => {
      // window.top.Ads.helpers.scrollWelcomeAd();
      // eslint-disable-next-line no-console
      console.info('Scroll Welcome Ad Container');
    };

    Object.defineProperty(window, 'Ads', {
      value: {
        helpers: {
          setSideBar,
          resetAll,
          setWelcomeAd,
          closeWelcomeAd,
          scrollWelcomeAd,
        },
      },
      writable: false,
    });
  }
};

/**
 * Used to move down sidebars, so they don't overlap logos on event pages.
 * Not in use anymore because banned RiAd ads on event pages.
 * Keeping it as might be useful someday.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const alignSidebarAdsVerticalPositions = (currentBreakpoint: string) => {
  const topNavBarHeight =
    document.getElementById('navbar')?.getBoundingClientRect()?.height ?? 96;
  const isExtraLargeOrGreater = currentBreakpoint === 'lg';
  const isEventPage = EVENT_PAGE_SLUGS.some((slug) =>
    window.location.pathname.startsWith(`/${slug}`),
  );

  if (!isEventPage && isExtraLargeOrGreater) {
    const leftSidebar =
      (document.getElementsByClassName('side-ad-box-left')[0] as HTMLElement) ??
      null;
    const rightSidebar =
      (document.getElementsByClassName(
        'side-ad-box-right',
      )[0] as HTMLElement) ?? null;

    const wideAdPosition = document
      .getElementById('wideboard-ad-1')
      ?.getBoundingClientRect();

    if (leftSidebar && rightSidebar && wideAdPosition) {
      const marginTop = wideAdPosition.y - topNavBarHeight;

      leftSidebar.style.marginTop = `${marginTop}px`;
      rightSidebar.style.marginTop = `${marginTop}px`;
    }
  }

  // TODO: Could be refactored probably to limit code duplication
  if (isEventPage) {
    const leftSidebar =
      (document.getElementsByClassName('side-ad-box-left')[0] as HTMLElement) ??
      null;
    const rightSidebar =
      (document.getElementsByClassName(
        'side-ad-box-right',
      )[0] as HTMLElement) ?? null;

    if (leftSidebar && rightSidebar) {
      const starNightLeftSideLogoHeight = 130;
      const marginTop = topNavBarHeight + starNightLeftSideLogoHeight;

      leftSidebar.style.marginTop = `${marginTop}px`;
      leftSidebar.style.top = `${marginTop}px`;
      rightSidebar.style.marginTop = `${marginTop}px`;
      rightSidebar.style.top = `${marginTop}px`;
    }
  }
};

export interface AdvertisementProps {
  slot: AdSlot;
  slotId?: string;
  additionalContainerClass?: string;
}

const Advertisement = forwardRef<HTMLDivElement, AdvertisementProps>(
  ({ slot, slotId, additionalContainerClass }, ref) => {
    if (slot === 'preroll_1') {
      return null;
    }

    const config = AdSlotsConfig[slot as AdSlot];

    if (!config) {
      return null;
    }

    const currentSlotId = slotId || config.container;
    const isChannelSwitcherAdSlot = isChannelSwitcherContainer(currentSlotId);
    const isChannelSwitcherDesktopSidebar =
      slot === 'side-right_ad_1' && isChannelSwitcherAdSlot;

    if (slot === 'top_special_1') {
      return (
        <div
          ref={ref}
          data-slotid={config.container}
          id={currentSlotId}
          className="ad-container"
        />
      );
    }

    if (['side-right_ad_1', 'right_ad_1', 'left_ad_1'].includes(slot)) {
      return (
        <div
          className={cls(
            'ad-sidebar-frame relative overflow-visible',
            isChannelSwitcherDesktopSidebar
              ? 'channel-switcher-side-ad flex w-[300px] min-h-[250px] max-h-[600px] shrink-0 items-center justify-center overflow-visible'
              : 'min-h-[250px] w-full',
          )}
        >
          <div
            ref={ref}
            data-slotid={config.container}
            id={currentSlotId || slotId || config.container}
            className={cls(
              'ad-container ad-container--sidebar relative z-10 flex shrink-0 justify-center',
              isChannelSwitcherDesktopSidebar
                ? 'max-h-[600px] max-w-[300px] min-h-[250px] min-w-[120px] w-auto items-center'
                : 'inset-0 h-full w-full',
            )}
          >
            {/* Advertisement here */}
          </div>
          <div
            className={cls(
              'ad-ech-placeholder absolute inset-0 flex items-end border rounded-2 p-4',
              isChannelSwitcherAdSlot ? 'border-white' : 'border-gray-1',
            )}
          >
            <p
              className={cls(
                'text-overline',
                isChannelSwitcherAdSlot ? 'text-white' : 'text-gray-1',
              )}
            >
              Werbung
            </p>
          </div>
        </div>
      );
    }

    return (
      <div
        className="ad-slot relative"
        style={{
          width: config.width,
          ...(config.minHeight
            ? { minHeight: config.minHeight }
            : { height: config.height }),
        }}
      >
        <div
          ref={ref}
          data-slotid={config.container}
          id={currentSlotId}
          className={cls(
            config.container,
            'ad-container relative inset-0 z-10 flex justify-center',
            additionalContainerClass,
          )}
        >
          {/* Advertisement here */}
        </div>

        <div
          className={cls(
            'ad-ech-placeholder absolute inset-0 flex items-end border rounded-2 p-4',
            isChannelSwitcherAdSlot ? 'border-white' : 'border-gray-1',
          )}
        >
          <p
            className={cls(
              'text-overline',
              isChannelSwitcherAdSlot ? 'text-white' : 'text-gray-1',
            )}
          >
            Werbung
          </p>
        </div>
      </div>
    );
  },
);

export default Advertisement;
